// Scene manifest (docs/contracts.md section 2). three.js coordinates, metres.

import { reverbProblems, type ReverbSpec } from '@lr3d/shared';
import { fieldProblems, fieldSpec, type FieldSpec } from '../outdoor/field';

export type V3 = [number, number, number];
export type TierName = 'desktop' | 'mobile';

export interface TierInfo {
  url: string;
  bytes?: number;
  triangles?: number;
  lightmap_px?: number;
}

export interface BoxObstacle {
  kind: 'box';
  id?: string;
  min: V3;
  max: V3;
}

export interface ScreenInfo {
  id: string;
  node: string;
  shape?: string;
  center?: V3;
  normal?: V3;
  radius?: number;
  /** "posters": the client draws posters on it like the monitor (contracts section 5); default: the item's video */
  content?: string;
  /** the letter on the client's static screen face (video/screens.ts); default "A" */
  letter?: string;
  /** rect screens (the monitor), metres */
  width?: number;
  height?: number;
}

/** A door to another room (contracts section 9). `to_room` / `to_spawn` are defaults; rooms.json links decide. */
export interface PortalInfo {
  id: string;
  /** glb node of the door or opening, if any */
  node?: string;
  /** centre of the opening on the wall plane, at floor height */
  position: V3;
  /** into the room */
  normal: V3;
  /** [width, height], metres */
  size: [number, number];
  /** where a player coming through this portal arrives */
  spawn?: { position: V3; yaw_deg: number };
  to_room?: string;
  to_spawn?: string;
  /** No door in the scene geometry: the client draws a dark doorway (placeholder scene). */
  marker?: boolean;
  /** "floor" (2026-10-28): an area on the floor (an opening with a ladder, a ladder up) instead of a door in a wall.
   *  `position` is the area's centre on the floor, `normal` up, `size` its x and z extent. */
  kind?: 'wall' | 'floor';
  /** floor portal: the marked area, three.js x, z */
  area?: { min: [number, number]; max: [number, number] };
  /** floor portal: height of the pick box above the floor (clicking the ladder or the opening) */
  height?: number;
  /** floor portal: the player's centre this close to the area (m) triggers */
  reach?: number;
}

export interface Manifest {
  version: number;
  scene_id: string;
  /** Build id (`<variant>-<cad commit>-<pipeline commit>`), builds since 2026-10-24. */
  build_id?: string;
  cad_project?: string;
  variant?: string;
  built_at?: string;
  source_commit?: string;
  pipeline_commit?: string;
  tiers: Partial<Record<TierName, TierInfo>>;
  /** Optional: 'none' | 'linear' | 'reinhard' | 'aces' | 'agx' | 'neutral'. Missing = none. */
  tone_mapping?: string;
  exposure?: number;
  spawn: { position: V3; yaw_deg: number };
  player: { eye_height: number; radius: number; walk_speed: number; run_speed: number };
  collision: { floor_y: number; walkable: [number, number][]; obstacles: BoxObstacle[] };
  /** lowpass_hz / reverb_send / reverb: the room's sound (audio/room-sound.ts); missing = no filter, default reverb.
   *  rooms.json audio.reverb wins over `reverb`. */
  audio: { sources: { id: string; position: V3; forward: V3 }[]; lowpass_hz?: number; reverb_send?: number; reverb?: ReverbSpec };
  reflection?: { probe_position: V3; env_url?: string | null };
  /** Clear colour behind the room, '#rrggbb' (sRGB): the median colour of the baked walls atlas, so a hairline gap
   * between two surfaces shows a wall-like tone (builds since 2026-10-27). Missing: DEFAULT_BACKGROUND. */
  background?: string;
  screens?: ScreenInfo[];
  portals?: PortalInfo[];
  /** An outdoor field generated in the client (2026-11-01, outdoor/field.ts; docs/plans/outdoor-spaces.md): no glb is
   *  fetched (the tiers' `url` is not read), the field repeats every size_m in x and z. Filled with defaults on load. */
  outdoor?: FieldSpec;
}

/** Background for manifests without `background`: a neutral warm mid-tone near the rooms' plaster as rendered. */
export const DEFAULT_BACKGROUND = 0x6d5a4e;

/** The scene background of a manifest (sRGB hex number). */
export function backgroundOf(m: Manifest): number {
  const v = /^#([0-9a-f]{6})$/i.exec(m.background ?? '');
  return v ? parseInt(v[1]!, 16) : DEFAULT_BACKGROUND;
}

export interface LoadedManifest {
  manifest: Manifest;
  /** Directory URL the tier urls are relative to, ending in '/'. */
  base: string;
}

/** Scene registry and build folders: same origin by default, R2 in production (VITE_SCENES_URL, docs/deploy.md). */
export const SCENES = (import.meta.env.VITE_SCENES_URL || `${import.meta.env.BASE_URL}scenes/`).replace(/\/*$/, '/');

async function tryLoad(id: string): Promise<LoadedManifest | null> {
  return loadManifestAt(`${SCENES}${id}/`);
}

/** The manifest in the build folder `base` (a URL ending in '/'), or null if missing or unusable. */
export async function loadManifestAt(base: string): Promise<LoadedManifest | null> {
  try {
    const res = await fetch(`${base}manifest.json`, { cache: 'no-cache' });
    if (!res.ok) return null;
    const type = res.headers.get('content-type') || '';
    if (!type.includes('json')) return null; // dev server answers unknown paths with index.html
    const manifest = (await res.json()) as Manifest;
    validate(manifest);
    return { manifest, base };
  } catch (e) {
    console.warn(`[scene] manifest ${base} unusable:`, e);
    return null;
  }
}

function validate(m: Manifest): void {
  if (m.version !== 1) throw new Error(`unsupported manifest version ${m.version}`);
  if (!m.tiers || (!m.tiers.desktop && !m.tiers.mobile)) throw new Error('manifest has no tiers');
  if (!m.collision?.walkable || m.collision.walkable.length < 3) throw new Error('manifest has no walkable polygon');
  m.collision.obstacles ??= [];
  m.audio ??= { sources: [] };
  if (m.audio.reverb !== undefined) {
    const p = reverbProblems(m.audio.reverb, 'audio.reverb');
    if (p.length) { console.warn('[scene] manifest reverb ignored:', p.join('; ')); delete m.audio.reverb; }
  }
  m.portals ??= [];
  if (m.outdoor !== undefined) {
    const p = fieldProblems(m.outdoor);
    if (p.length) throw new Error(`manifest outdoor: ${p.join('; ')}`);
    m.outdoor = fieldSpec(m.outdoor);
  }
}

/** Explicit scene id, else the real scene, else the placeholder. */
export async function loadManifest(sceneId: string | null): Promise<LoadedManifest> {
  const order = sceneId ? [sceneId] : ['listening_room', 'placeholder'];
  for (const id of order) {
    const m = await tryLoad(id);
    if (m) return m;
  }
  throw new Error(`no usable scene manifest (tried ${order.join(', ')})`);
}

/** Pick the quality tier: URL override, else mobile on coarse pointers, small screens or low memory. */
export function pickTier(m: Manifest, override: TierName | null): TierName {
  const has = (t: TierName) => Boolean(m.tiers[t]);
  if (override && has(override)) return override;
  const coarse = matchMedia('(pointer: coarse)').matches && !matchMedia('(pointer: fine)').matches;
  const small = Math.min(screen.width, screen.height) < 600;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const lowMem = typeof mem === 'number' && mem <= 4;
  const want: TierName = coarse || small || lowMem ? 'mobile' : 'desktop';
  return has(want) ? want : want === 'mobile' ? 'desktop' : 'mobile';
}
